import React, { useState, useEffect, useRef, useMemo } from "react";
import Modal from "react-modal";
import { X, RotateCcw } from "lucide-react";
import { toast } from "react-toastify";
import { adminServices } from "../services/AdminServices";
import largeNoImage from "../icons/inventory/item-master/large-noimage.png";
import editIcon from "../icons/inventory/dashboard/edit.png";

if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

const parsePrice = (val) => {
  if (val === "" || val == null || val === undefined) return 0;
  const str = String(val).replace(/[₱Pp,\s]/g, "");
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

// Base shape used when clearing the form (e.g. on close)
const initialFormState = {
  productId: 0,
  image: "",
  sku: "",
  barcode: "",
  itemTag: "Regular",
  name: "",
  brand: "",
  variant: "",
  largestUOM: "",
  smallestUOM: "",
  details: "",
  category: "",
  productClassificationId: "",
  productSubclassificationId: "",
  status: "",
  maxOrderQty: "",
  markup: "",
  marketPrice: "",
  supplierToIpxUom: "",
  supplierToIpxPrice: 0,
  ipxToBigStoreUom: "",
  ipxToBigStorePrice: 0,
  ipxToSmallStoreUom: "",
  ipxToSmallStorePrice: 0,
  ipxToPdlUom: "",
  ipxToPdlPrice: 0,
  bigStoreToSmallStoreUom: "",
  bigStoreToSmallStorePrice: 0,
  bigStoreToPdlUom: "",
  bigStoreToPdlPrice: 0,
  smallStoreToPdlUom: "",
  smallStoreToPdlPrice: 0
};

const EditProductModal = ({ product, isOpen, onClose, onSave, categories = [], classifications = [], subclassifications = [] }) => {
  const [activeTab, setActiveTab] = useState("subSku");
  const [form, setForm] = useState({
    productId: 0,
    image: "",
    sku: "",
    barcode: "",
    itemTag: "Regular",
    name: "",
    brand: "",
    variant: "",
    largestUOM: "",
    smallestUOM: "",
    details: "",
    category: "",
    productClassificationId: "",
    productSubclassificationId: "",
    status: "",
    maxOrderQty: "",
    markup: "",
    marketPrice: "",
    supplierToIpxUom: "",
    supplierToIpxPrice: 0,
    ipxToBigStoreUom: "",
    ipxToBigStorePrice: 0,
    ipxToSmallStoreUom: "",
    ipxToSmallStorePrice: 0,
    ipxToPdlUom: "",
    ipxToPdlPrice: 0,
    bigStoreToSmallStoreUom: "",
    bigStoreToSmallStorePrice: 0,
    bigStoreToPdlUom: "",
    bigStoreToPdlPrice: 0,
    smallStoreToPdlUom: "",
    smallStoreToPdlPrice: 0
  });
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef(null);
  const [subSkus, setSubSkus] = useState([]);
  const [uomConversions, setUomConversions] = useState([]);
  const [uomConversionForm, setUomConversionForm] = useState({ baseUnit: "", convertedUnit: "", baseAmount: "" });
  const [addingUomConversion, setAddingUomConversion] = useState(false);
  const [uoms, setUoms] = useState([]);
  const [uomsLoading, setUomsLoading] = useState(false);

  const initialAddSubSkuForm = { sku: "", itemDescription: "", barcode: "", uomId: "", status: "" };
  const [showAddSubSkuModal, setShowAddSubSkuModal] = useState(false);
  const [addSubSkuForm, setAddSubSkuForm] = useState(initialAddSubSkuForm);
  const [addingSubSku, setAddingSubSku] = useState(false);
  const [addSubSkuError, setAddSubSkuError] = useState("");
  const [editingSubSku, setEditingSubSku] = useState(null); // holds the row being edited

  const [imageProduct, setImageProduct] = useState(null);

  useEffect(() => {
    if (!isOpen || !product) return;

    // Load UOMs once; don't block populating the form if we already have them
    if (!uomsLoading && (!Array.isArray(uoms) || uoms.length === 0)) {
      setUomsLoading(true);
      adminServices
        .getUoms()
        .then((res) => {
          const payload = res?.data ?? res;
          const list = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.items)
                ? payload.items
                : [];
          setUoms(list);
        })
        .catch((err) => {
          console.error("Failed to load UOMs", err);
          setUoms([]);
        })
        .finally(() => setUomsLoading(false));
    }
    const orig = product.originalData || product;
    const val = (obj, ...keys) => {
      for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
      }
      return undefined;
    };
    // Safely convert a value that may be a UOM object or numeric ID to its name string
    const strVal = (...args) => {
      const v = val(...args);
      if (v === undefined || v === null) return "";
      if (typeof v === "object") return v.name ?? "";
      return String(v);
    };
    // Convert a UOM value (object, numeric ID, or name string) to the UOM name
    const localUomOptions = Array.isArray(uoms)
      ? uoms.map((u) => ({
          id: u?.id ?? u?.uomId ?? u?.uom_id ?? u?.UomId ?? u?.ID ?? u?.Id,
          name: u?.name ?? u?.uomName ?? u?.uom_name ?? u?.UomName ?? u?.Name
        })).filter((x) => x.id != null && String(x.name || "").trim())
      : [];
    const uomName = (v) => {
      if (!v && v !== 0) return "";
      if (typeof v === "object") return v.name ?? "";
      const found = localUomOptions.find((u) => String(u.id) === String(v));
      return found?.name ?? String(v);
    };

    // productSettings[0] holds all pricing/UOM data in the new API shape
    const settings = Array.isArray(orig.productSettings) && orig.productSettings.length > 0
      ? orig.productSettings[0]
      : null;

    // subclassficationId is the API's (typo'd) subclassification FK
    const subClassificationId =
      val(orig, "productSubclassificationId", "product_subclassification_id", "subclassficationId") ??
      orig?.productSubclassification?.id ??
      product.productSubclassificationId ??
      product.subclassficationId;

    const subClassificationName =
      val(orig, "subClassification", "subCategory") ??
      product.subCategory ??
      orig?.productSubclassification?.name ??
      "";

    // Derive classification from the subclassification entry in our list
    const subclassObj = subClassificationId
      ? subclassifications.find((x) => x.id === Number(subClassificationId))
      : null;
    const classificationId =
      val(orig, "productClassificationId", "product_classification_id") ??
      orig?.productClassification?.id ??
      product.productClassificationId ??
      subclassObj?.parent_id ??
      subclassObj?.parentId;

    const classificationName =
      val(orig, "classification") ??
      product.classification ??
      orig?.productClassification?.name ??
      "";

    // Derive category from subclassification list entry
    const catIdFromSubclass =
      subclassObj?.product_category_id ?? subclassObj?.productCategoryId;
    const categoryNameFromSubclass = catIdFromSubclass
      ? categories.find((c) => c.id === catIdFromSubclass)?.name ?? ""
      : "";
    const categoryName =
      val(orig, "category") ?? product.category ?? categoryNameFromSubclass ?? "";

    const catId = categories.find((c) => c.name === categoryName)?.id ?? catIdFromSubclass;

    const resolveClassificationId = () => {
      if (classificationId) return Number(classificationId);
      if (classificationName && catId) {
        const c = classifications.find(
          (x) => (x.product_category_id ?? x.productCategoryId) === catId && (x.name ?? "") === classificationName
        );
        return c?.id ?? "";
      }
      return "";
    };
    const resolveSubClassificationId = () => {
      if (subClassificationId) return Number(subClassificationId);
      if (subClassificationName && catId) {
        const sc = subclassifications.find(
          (x) => (x.product_category_id ?? x.productCategoryId) === catId && (x.name ?? "") === subClassificationName
        );
        return sc?.id ?? "";
      }
      return "";
    };

    const productId = orig.id ?? orig.productId ?? product.id ?? 0;

    setImageProduct("");

    

    setForm({
      productId,
      image: val(orig, "image", "imageUrl", "image_url") ?? product.image ?? product.imageUrl ?? "",
      sku: val(orig, "sku") ?? product.sku ?? "",
      barcode: val(orig, "barcode") ?? product.barcode ?? "",
      itemTag: val(orig, "itemTag", "item_tag", "type") ?? "Regular",
      name: val(orig, "name") ?? product.name ?? "",
      brand: val(orig, "brand") ?? product.brand ?? "",
      variant: val(orig, "variant") ?? product.variant ?? "",
      largestUOM: strVal(orig, "largestUOM", "largest_uom") || (typeof product.largestUOM === "object" ? (product.largestUOM?.name ?? "") : (product.largestUOM ?? "")),
      smallestUOM: strVal(orig, "smallestUOM", "smallest_uom") || (typeof product.smallestUOM === "object" ? (product.smallestUOM?.name ?? "") : (product.smallestUOM ?? "")),
      details: val(orig, "details") ?? val(orig, "name") ?? product.details ?? "",
      status: val(orig, "status") ?? "",
      maxOrderQty: val(orig, "maxOrderQty", "max_order_qty", "regulatedLimit") ?? "",
      category: categoryName,
      productClassificationId: resolveClassificationId(),
      productSubclassificationId: resolveSubClassificationId(),
      markup: ((settings?.markup ?? val(orig, "markup")) ?? "")?.toString?.() ?? "",
      marketPrice: (parsePrice(settings?.marketPrice ?? val(orig, "marketPrice") ?? product.marketPrice) || "").toString(),
      supplierToIpxUom: uomName(settings?.supplierToIpxUom ?? val(orig, "supplierToIpxUom")),
      supplierToIpxPrice: settings?.supplierToIpxPrice ?? val(orig, "supplierToIpxPrice") ?? 0,
      ipxToBigStoreUom: uomName(settings?.ipxToBigStoreUom ?? val(orig, "ipxToBigStoreUom")),
      ipxToBigStorePrice: settings?.ipxToBigStorePrice ?? val(orig, "ipxToBigStorePrice") ?? 0,
      ipxToSmallStoreUom: uomName(settings?.ipxToSmallStoreUom ?? val(orig, "ipxToSmallStoreUom")),
      ipxToSmallStorePrice: settings?.ipxToSmallStorePrice ?? val(orig, "ipxToSmallStorePrice") ?? 0,
      ipxToPdlUom: uomName(settings?.ipxToPdlUom ?? val(orig, "ipxToPdlUom")),
      ipxToPdlPrice: settings?.ipxToPdlPrice ?? val(orig, "ipxToPdlPrice") ?? 0,
      bigStoreToSmallStoreUom: uomName(settings?.bigStoreToSmallStoreUom ?? val(orig, "bigStoreToSmallStoreUom")),
      bigStoreToSmallStorePrice: settings?.bigStoreToSmallStorePrice ?? val(orig, "bigStoreToSmallStorePrice") ?? 0,
      bigStoreToPdlUom: uomName(settings?.bigStoreToPdlUom ?? val(orig, "bigStoreToPdlUom")),
      bigStoreToPdlPrice: settings?.bigStoreToPdlPrice ?? val(orig, "bigStoreToPdlPrice") ?? 0,
      smallStoreToPdlUom: uomName(settings?.smallStoreToPdlUom ?? val(orig, "smallStoreToPdlUom")),
      smallStoreToPdlPrice: settings?.smallStoreToPdlPrice ?? val(orig, "smallStoreToPdlPrice") ?? 0
    });
    setSubSkus(orig?.subSkus ?? orig?.sub_skus ?? product?.subSkus ?? []);
    setUomConversions(orig?.uomConversions ?? orig?.uom_conversions ?? product?.uomConversions ?? []);

    // Load latest Sub SKU list and UOM conversions from API
    if (productId) {
      adminServices.storeProcedure2("getProductImage", { productId: Number(productId) })
        .then((res) => {
          setImageProduct(res?.data?.[0]?.image ?? "");
        })
        .catch((err) => {
          console.error("Failed to load product image", err);
          setImageProduct("");
        });

      adminServices
        .getSubProductsByProduct(productId)
        .then((res) => {
          const raw = Array.isArray(res?.data) ? res.data : [];
          if (raw.length > 0) {
            setSubSkus(raw);
          }
        })
        .catch((err) => {
          console.error("Failed to load sub products for product", productId, err);
        });
      adminServices
        .getProductUomConversionsByProduct(productId)
        .then((res) => {
          const raw = Array.isArray(res?.data) ? res.data : res ?? [];
          setUomConversions(raw);
        })
        .catch((err) => {
          console.error("Failed to load UOM conversions for product", productId, err);
        });

      
    }
  }, [isOpen, product, categories, classifications, subclassifications, uoms, uomsLoading]);

  const uomOptions = Array.isArray(uoms)
    ? uoms
        .map((u) => ({
          id: u?.id ?? u?.uomId ?? u?.uom_id ?? u?.UomId ?? u?.ID ?? u?.Id,
          name: u?.name ?? u?.uomName ?? u?.uom_name ?? u?.UomName ?? u?.Name
        }))
        .filter((x) => x.id != null && String(x.name || "").trim())
    : [];

  const pricingUomOptions = useMemo(() => {
    if (!Array.isArray(uomConversions) || uomConversions.length === 0) return [];
    const names = new Set();
    uomConversions.forEach((row) => {
      const candidates = [
        row?.baseUnitNavigation?.name,
        row?.convertedUnitNavigation?.name,
        typeof row?.baseUnit === "object" ? row?.baseUnit?.name : null,
        typeof row?.convertedUnit === "object" ? row?.convertedUnit?.name : null,
        typeof row?.baseUnit === "string" ? row.baseUnit : null,
        typeof row?.convertedUnit === "string" ? row.convertedUnit : null,
      ];
      candidates.forEach((n) => {
        if (n != null && String(n).trim() !== "") names.add(String(n).trim());
      });
    });
    return uomOptions.filter((u) => names.has(u.name));
  }, [uomConversions, uomOptions]);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Full = reader.result; // e.g. "data:image/jpeg;base64,/9j/..."

      const productId = form.productId || product?.id || product?.originalData?.id;
      if (!productId) {
        toast.error("Product ID is missing.");
        return;
      }

      setImageUploading(true);
      try {
        await adminServices.storeProcedure2("saveProductImage", {
          productId: Number(productId),
          imageData: base64Full
        });
        // Update preview with the full data URL
        setImageProduct(base64Full);
        handleChange("image", base64Full);
        toast.success("Image uploaded successfully.");
      } catch (err) {
        console.error("Failed to upload image", err);
        toast.error(err?.response?.data?.message || "Failed to upload image.");
      } finally {
        setImageUploading(false);
        if (imageInputRef.current) imageInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "category") {
        next.productClassificationId = "";
        next.productSubclassificationId = "";
      } else if (field === "productClassificationId") {
        next.productSubclassificationId = "";
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm(initialFormState);
    setSubSkus([]);
    setUomConversions([]);
    setUomConversionForm({ baseUnit: "", convertedUnit: "", baseAmount: "" });
    setShowAddSubSkuModal(false);
    setAddSubSkuForm(initialAddSubSkuForm);
    setAddSubSkuError("");
    setEditingSubSku(null);
    setActiveTab("subSku");
    onClose();
  };

  const refetchUomConversions = () => {
    const productId = form.productId || product?.id || product?.originalData?.id;
    if (!productId) return;
    adminServices
      .getProductUomConversionsByProduct(productId)
      .then((res) => {
        const raw = Array.isArray(res?.data) ? res.data : res ?? [];
        setUomConversions(raw);
      })
      .catch((err) => console.error("Failed to refetch UOM conversions", err));
  };

  const handleAddUomConversion = async (e) => {
    e.preventDefault();
    const productId = form.productId || product?.id || product?.originalData?.id;
    if (!productId) return;
    const baseUnit = Number(uomConversionForm.baseUnit);
    const convertedUnit = Number(uomConversionForm.convertedUnit);
    const baseAmount = Number(uomConversionForm.baseAmount);
    if (!baseUnit || !convertedUnit || baseAmount <= 0) return;
    setAddingUomConversion(true);
    try {
      await adminServices.createProductUomConversion({
        productId: Number(productId),
        baseUnit,
        baseAmount,
        convertedUnit
      });
      setUomConversionForm({ baseUnit: "", convertedUnit: "", baseAmount: "" });
      refetchUomConversions();
    } catch (err) {
      console.error("Failed to add UOM conversion", err);
    } finally {
      setAddingUomConversion(false);
    }
  };

  const handleAddSubSkuChange = (field, value) => {
    setAddSubSkuForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSubSkuConfirm = async () => {
    setAddSubSkuError("");
    const productId = form.productId || product?.id || product?.originalData?.id;
    if (!productId) {
      setAddSubSkuError("Product ID is missing.");
      return;
    }
    if (!addSubSkuForm.sku.trim()) {
      setAddSubSkuError("SKU Code is required.");
      return;
    }
    setAddingSubSku(true);
    const payload = {
      sku: addSubSkuForm.sku.trim(),
      itemDescription: addSubSkuForm.itemDescription.trim(),
      barcode: addSubSkuForm.barcode.trim(),
      uomId: addSubSkuForm.uomId ? Number(addSubSkuForm.uomId) : 0,
      status: addSubSkuForm.status.trim(),
      productId: Number(productId)
    };
    try {
      if (editingSubSku) {
        await adminServices.updateSubProduct(editingSubSku.subProductId ?? editingSubSku.id, payload);
      } else {
        await adminServices.createSubProduct(payload);
      }
      setAddSubSkuForm(initialAddSubSkuForm);
      setEditingSubSku(null);
      setShowAddSubSkuModal(false);
      adminServices
        .getSubProductsByProduct(productId)
        .then((res) => {
          const refreshedList = Array.isArray(res?.data) ? res.data : [];
          setSubSkus(refreshedList);
        })
        .catch((err) => console.error("Failed to refresh sub SKUs", err));
    } catch (err) {
      console.error("Failed to save sub product", err);
      setAddSubSkuError(err?.response?.data?.message ?? err?.message ?? "Failed to save sub SKU.");
    } finally {
      setAddingSubSku(false);
    }
  };

  if (!product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: { backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1000 },
        content: {
          position: "relative",
          backgroundColor: "#fff",
          borderRadius: "12px",
          maxWidth: "1040px",
          width: "100%",
          margin: "auto",
          padding: "0",
          outline: "none",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.35)",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }
      }}
      contentLabel="Edit Product"
    >
      <div style={{ padding: "20px 28px 24px", overflowY: "auto", flex: 1, background: "#ffffff" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "20px",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: "8px"
          }}
        >
          <div style={{ display: "flex", gap: "20px", flex: 1 }}>
            <div style={{ flexShrink: 0 }}>
              <div
                style={{
                  position: "relative",
                  width: "260px",
                  height: "260px",
                  background: "#f9fafb",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  marginTop: "10px"
                }}
              >
                <img
                  src={imageProduct || form.image || largeNoImage}
                  alt="Product"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", opacity: 0.9 }}
                  onError={(e) => {
                    if (e.target.src !== largeNoImage) {
                      e.target.src = largeNoImage;
                    }
                  }}
                />
                {imageUploading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.8rem",
                      color: "#374151"
                    }}
                  >
                    Uploading...
                  </div>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                <button
                  type="button"
                  title="Upload image"
                  disabled={imageUploading}
                  onClick={() => imageInputRef.current?.click()}
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    right: "10px",
                    width: "30px",
                    height: "30px",
                    borderRadius: "999px",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    cursor: imageUploading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 6px 12px rgba(15,23,42,0.18)"
                  }}
                >
                  <RotateCcw size={14} color="#4b5563" />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: "4px 0 2px", fontSize: "1.2rem", fontWeight: 600, color: "#111827" }}>
                {form.name || "Product name"}
              </h2>
              <p style={{ margin: "0 0 10px", fontSize: "0.8rem", color: "#6b7280" }}>
                {form.sku || "SKU-000"} {form.barcode ? `• ${form.barcode}` : ""}
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,minmax(0,1fr))",
                  gap: "10px 18px",
                  marginTop: "10px"
                }}
              >
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>SKU Code</label>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => handleChange("sku", e.target.value)}
                    readOnly
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px",
                      backgroundColor: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "default",
                      outline: "none"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Bar Code</label>
                  <input
                    type="text"
                    value={form.barcode}
                    onChange={(e) => handleChange("barcode", e.target.value)}
                    readOnly
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px",
                      backgroundColor: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "default",
                      outline: "none"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Item Tag (BuCor)</label>
                  <input
                    type="text"
                    value={form.itemTag}
                    onChange={(e) => handleChange("itemTag", e.target.value)}
                    readOnly
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px",
                      backgroundColor: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "default",
                      outline: "none"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Item Description</label>
                  <input
                    type="text"
                    value={form.details}
                    onChange={(e) => handleChange("details", e.target.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Status</label>
                  <input
                    type="text"
                    value={form.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    placeholder="-"
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Max. Order QTY (Regulated)</label>
                  <input
                    type="text"
                    value={form.maxOrderQty}
                    onChange={(e) => handleChange("maxOrderQty", e.target.value)}
                    placeholder="-"
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      background: "#fff",
                      marginTop: "2px"
                    }}
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                    {form.category && !categories.some((cc) => cc.name === form.category) && (
                      <option value={form.category}>{form.category}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Classification</label>
                  <select
                    value={form.productClassificationId}
                    onChange={(e) =>
                      handleChange("productClassificationId", e.target.value ? Number(e.target.value) : "")
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      background: "#fff",
                      marginTop: "2px"
                    }}
                  >
                    <option value="">Select classification</option>
                    {(classifications || [])
                      .filter(
                        (c) =>
                          (c.product_category_id ?? c.productCategoryId) ===
                          categories.find((cc) => cc.name === form.category)?.id
                      )
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name ?? "-"}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Sub-Classification</label>
                  <select
                    value={form.productSubclassificationId}
                    onChange={(e) =>
                      handleChange("productSubclassificationId", e.target.value ? Number(e.target.value) : "")
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      background: "#fff",
                      marginTop: "2px"
                    }}
                  >
                    <option value="">Select sub-classification</option>
                    {(subclassifications || [])
                      .filter(
                        (sc) =>
                          (sc.product_category_id ?? sc.productCategoryId) ===
                            categories.find((cc) => cc.name === form.category)?.id &&
                          (sc.parent_id ?? sc.parentId) === Number(form.productClassificationId)
                      )
                      .map((sc) => (
                        <option key={sc.id} value={sc.id}>
                          {sc.name ?? "-"}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Brand</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => handleChange("brand", e.target.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Variant</label>
                  <input
                    type="text"
                    value={form.variant}
                    onChange={(e) => handleChange("variant", e.target.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      marginTop: "2px"
                    }}
                  />
                </div>
                {/* <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Largest UOM</label>
                  <select
                    value={form.largestUOM}
                    onChange={(e) => handleChange("largestUOM", e.target.value)}
                    disabled={uomsLoading}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      background: "#fff",
                      marginTop: "2px"
                    }}
                  >
                    <option value="">{uomsLoading ? "Loading..." : "Select UOM"}</option>
                    {uomOptions.map((u) => (
                      <option key={u.id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                    {form.largestUOM && !uomOptions.some((x) => x.name === form.largestUOM) && (
                      <option value={form.largestUOM}>{form.largestUOM}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.72rem", color: "#6b7280" }}>Smallest UOM</label>
                  <select
                    value={form.smallestUOM}
                    onChange={(e) => handleChange("smallestUOM", e.target.value)}
                    disabled={uomsLoading}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 9px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      background: "#fff",
                      marginTop: "2px"
                    }}
                  >
                    <option value="">{uomsLoading ? "Loading..." : "Select UOM"}</option>
                    {uomOptions.map((u) => (
                      <option key={u.id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                    {form.smallestUOM && !uomOptions.some((x) => x.name === form.smallestUOM) && (
                      <option value={form.smallestUOM}>{form.smallestUOM}</option>
                    )}
                  </select>
                </div> */}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px",
              marginLeft: "16px",
              color: "#6b7280"
            }}
          >
            <X size={22} />
          </button>
        </div>

        <div
          style={{
            marginBottom: "16px",
            background: "#fff",
            padding: "0 18px",
            borderRadius: "10px 10px 0 0"
          }}
        >
          <div style={{ display: "flex", gap: "24px" }}>
            {["subSku", "uomConversion", "pricing"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "12px 0",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: activeTab === tab ? "#002347" : "#6b7280",
                  borderBottom: activeTab === tab ? "2px solid #002347" : "2px solid transparent",
                  marginBottom: "-1px"
                }}
              >
                {tab === "subSku" ? "Sub SKU" : tab === "uomConversion" ? "UOM Conversion" : "Pricing"}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {activeTab === "subSku" && (
            <div
              style={{
                marginBottom: "24px",
                background: "#fff",
                borderRadius: "0 0 10px 10px",
                boxShadow: "0 6px 14px rgba(15,23,42,0.06)",
                overflow: "hidden"
              }}
            >
              {/* Add Sub SKU Button */}
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 2px 0", marginBottom: "10px" }}>
                <button
                  type="button"
                  onClick={() => {
                    setAddSubSkuForm(initialAddSubSkuForm);
                    setAddSubSkuError("");
                    setShowAddSubSkuModal(true);
                  }}
                  style={{
                    padding: "8px 18px",
                    border: "none",
                    borderRadius: "6px",
                    background: "#002347",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: 500
                  }}
                >
                  + Add Sub SKU
                </button>
              </div>

              {/* Add Sub SKU Inline Modal */}
              {showAddSubSkuModal && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.55)",
                    zIndex: 2000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "12px",
                      width: "100%",
                      maxWidth: "480px",
                      padding: "28px 32px 24px",
                      boxShadow: "0 20px 50px rgba(15,23,42,0.3)",
                      position: "relative"
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 22px",
                        fontSize: "1.05rem",
                        fontWeight: 600,
                        color: "#111827",
                        textAlign: "center"
                      }}
                    >
                      {editingSubSku ? "Edit Sub SKU" : "Add Sub SKU"}
                    </h3>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                      <div>
                        <label style={{ fontSize: "0.78rem", color: "#374151", display: "block", marginBottom: "5px" }}>
                          SKU Code <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={addSubSkuForm.sku}
                          onChange={(e) => handleAddSubSkuChange("sku", e.target.value)}
                          placeholder="Enter SKU"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: "0.78rem", color: "#374151", display: "block", marginBottom: "5px" }}>
                          Bar Code
                        </label>
                        <input
                          type="text"
                          value={addSubSkuForm.barcode}
                          onChange={(e) => handleAddSubSkuChange("barcode", e.target.value)}
                          placeholder="Enter barcode"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>

                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={{ fontSize: "0.78rem", color: "#374151", display: "block", marginBottom: "5px" }}>
                          Item Description
                        </label>
                        <input
                          type="text"
                          value={addSubSkuForm.itemDescription}
                          onChange={(e) => handleAddSubSkuChange("itemDescription", e.target.value)}
                          placeholder="Enter item description"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: "0.78rem", color: "#374151", display: "block", marginBottom: "5px" }}>
                          Status
                        </label>
                        <select
                          value={addSubSkuForm.status}
                          onChange={(e) => handleAddSubSkuChange("status", e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            boxSizing: "border-box",
                            background: "#fff"
                          }}
                        >
                          <option value="">Select status</option>
                          <option value="ACTIVE">Active</option>
                          <option value="INACTIVE">Inactive</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ fontSize: "0.78rem", color: "#374151", display: "block", marginBottom: "5px" }}>
                          UOM
                        </label>
                        <select
                          value={addSubSkuForm.uomId}
                          onChange={(e) => handleAddSubSkuChange("uomId", e.target.value)}
                          disabled={uomsLoading}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            boxSizing: "border-box",
                            background: "#fff"
                          }}
                        >
                          <option value="">{uomsLoading ? "Loading..." : "Select UOM"}</option>
                          {uomOptions.map((uomOption) => (
                            <option key={uomOption.id} value={uomOption.id}>
                              {uomOption.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {addSubSkuError && (
                      <p style={{ color: "#ef4444", fontSize: "0.8rem", margin: "12px 0 0", textAlign: "center" }}>
                        {addSubSkuError}
                      </p>
                    )}

                    <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddSubSkuModal(false);
                          setAddSubSkuForm(initialAddSubSkuForm);
                          setAddSubSkuError("");
                          setEditingSubSku(null);
                        }}
                        disabled={addingSubSku}
                        style={{
                          flex: 1,
                          padding: "10px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          background: "#fff",
                          cursor: addingSubSku ? "not-allowed" : "pointer",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: "#374151"
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddSubSkuConfirm}
                        disabled={addingSubSku}
                        style={{
                          flex: 1,
                          padding: "10px",
                          border: "none",
                          borderRadius: "6px",
                          background: addingSubSku ? "#9ca3af" : "#002347",
                          color: "#fff",
                          cursor: addingSubSku ? "not-allowed" : "pointer",
                          fontSize: "0.875rem",
                          fontWeight: 500
                        }}
                      >
                        {addingSubSku ? "Adding..." : "Confirm"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ background: "#002347", color: "#fff" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Active</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>SKU</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Item Description</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>UOM</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Bar Code</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subSkus.length > 0 ? subSkus.map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #e5e7eb", background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <div
                          style={{
                            width: "40px",
                            height: "22px",
                            borderRadius: "999px",
                            background: (row.isActive ?? row.active ?? true) ? "#10b981" : "#e5e7eb",
                            position: "relative"
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "3px",
                              left: (row.isActive ?? row.active ?? true) ? "20px" : "4px",
                              width: "16px",
                              height: "16px",
                              borderRadius: "999px",
                              background: "#fff",
                              boxShadow: "0 1px 3px rgba(15,23,42,0.35)",
                              transition: "left 150ms ease-in-out"
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>{row.sku ?? row.SKU ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.itemDescription ?? row.description ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{typeof row.uom === "object" ? (row.uom?.name ?? "-") : (row.uom ?? (typeof row.UOM === "object" ? (row.UOM?.name ?? "-") : (row.UOM ?? "-")))}</td>
                      <td style={{ padding: "10px 12px" }}>{row.barcode ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSubSku(row);
                            setAddSubSkuForm({
                              sku: row.sku ?? row.SKU ?? "",
                              itemDescription: row.itemDescription ?? row.description ?? "",
                              barcode: row.barcode ?? "",
                              uomId: row.uomId ?? row.uom?.id ?? "",
                              status: row.status ?? ""
                            });
                            setAddSubSkuError("");
                            setShowAddSubSkuModal(true);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#1E56A0",
                            cursor: "pointer",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <img src={editIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>No sub SKUs</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "uomConversion" && (
            <div style={{ marginBottom: "24px" }}>
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "16px 24px", alignItems: "end", marginBottom: "24px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", color: "#6b7280", marginBottom: "6px" }}>Base Unit</label>
                    <select
                      value={uomConversionForm.baseUnit}
                      onChange={(e) => setUomConversionForm((p) => ({ ...p, baseUnit: e.target.value }))}
                      disabled={uomsLoading}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontSize: "0.875rem", boxSizing: "border-box", background: "#fff" }}
                    >
                      <option value="">{uomsLoading ? "Loading..." : "Select base unit"}</option>
                      {uomOptions.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", color: "#6b7280", marginBottom: "6px" }}>Converted Unit</label>
                    <select
                      value={uomConversionForm.convertedUnit}
                      onChange={(e) => setUomConversionForm((p) => ({ ...p, convertedUnit: e.target.value }))}
                      disabled={uomsLoading}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontSize: "0.875rem", boxSizing: "border-box", background: "#fff" }}
                    >
                      <option value="">{uomsLoading ? "Loading..." : "Select converted unit"}</option>
                      {uomOptions.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", color: "#6b7280", marginBottom: "6px" }}>Base Amount</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="0"
                      value={uomConversionForm.baseAmount}
                      onChange={(e) => setUomConversionForm((p) => ({ ...p, baseAmount: e.target.value }))}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontSize: "0.875rem", boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={handleAddUomConversion}
                      disabled={addingUomConversion || !form.productId}
                      style={{ padding: "10px 25px", border: "none", borderRadius: "6px", background: form.productId && !addingUomConversion ? "#002347" : "#9ca3af", color: "#fff", cursor: form.productId && !addingUomConversion ? "pointer" : "not-allowed", fontSize: "0.875rem", fontWeight: 500 }}
                    >
                      {addingUomConversion ? "Adding..." : "+ Add"}
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                  <thead>
                    <tr style={{ background: "#002347", color: "#fff" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Base Amount</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Base Unit</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Converted Unit</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Converted Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uomConversions.length > 0 ? uomConversions.map((row, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #e5e7eb", background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                        <td style={{ padding: "12px 16px" }}>{"1"}</td>
                        <td style={{ padding: "12px 16px" }}>{row.baseUnitNavigation?.name ?? (typeof row.baseUnit === "object" ? (row.baseUnit?.name ?? "-") : (row.baseUnit ?? row.baseUnitId ?? "-"))}</td>
                        <td style={{ padding: "12px 16px" }}>{row.convertedUnitNavigation?.name ?? (typeof row.convertedUnit === "object" ? (row.convertedUnit?.name ?? "-") : (row.convertedUnit ?? row.convertedUnitId ?? "-"))}</td>
                        <td style={{ padding: "12px 16px" }}>{row.baseAmount ?? "-"}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={4} style={{ padding: "32px 16px", textAlign: "center", color: "#6b7280", fontSize: "0.875rem" }}>No UOM conversions. Add one above.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "pricing" && (
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", gap: "24px", marginBottom: "20px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "0.875rem", color: "#374151", display: "block", marginBottom: "8px" }}>
                    Market Price (₱)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={form.marketPrice}
                    onChange={(e) => handleChange("marketPrice", e.target.value)}
                    placeholder="0"
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontSize: "0.875rem" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "0.875rem", color: "#374151", display: "block", marginBottom: "8px" }}>
                    Profit Margin (%)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={form.markup}
                    onChange={(e) => handleChange("markup", e.target.value)}
                    placeholder="0"
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontSize: "0.875rem" }}
                  />
                </div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                <thead>
                  <tr style={{ background: "#002347", color: "#fff" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Pricing Tier</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>UOM</th>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Price (₱)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Supplier to IPX", uom: "supplierToIpxUom", price: "supplierToIpxPrice" },
                    { label: "IPX to Big Store", uom: "ipxToBigStoreUom", price: "ipxToBigStorePrice" },
                    { label: "IPX to Small Store", uom: "ipxToSmallStoreUom", price: "ipxToSmallStorePrice" },
                    { label: "IPX to PDL", uom: "ipxToPdlUom", price: "ipxToPdlPrice" },
                    { label: "Big Store to Small Store", uom: "bigStoreToSmallStoreUom", price: "bigStoreToSmallStorePrice" },
                    { label: "Big Store to PDL", uom: "bigStoreToPdlUom", price: "bigStoreToPdlPrice" },
                    { label: "Small Store to PDL", uom: "smallStoreToPdlUom", price: "smallStoreToPdlPrice" }
                  ].map(({ label, uom, price }) => (
                    <tr key={uom} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "10px 12px" }}>{label}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <select
                          value={form[uom]}
                          onChange={(e) => handleChange(uom, e.target.value)}
                          disabled={uomsLoading}
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: "4px", background: "#fff", fontSize: "0.875rem" }}
                        >
                          <option value="">{uomsLoading ? "Loading..." : "Select UOM"}</option>
                          {pricingUomOptions.map((u) => (
                            <option key={u.id} value={u.name}>{u.name}</option>
                          ))}
                          {form[uom] && !pricingUomOptions.some((x) => x.name === form[uom]) && (
                            <option value={form[uom]}>{form[uom]}</option>
                          )}
                        </select>
                      </td>
                      <td style={{ padding: "10px 12px" }}><input type="number" step="any" value={form[price]} onChange={(e) => handleChange(price, parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: "4px" }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: "8px 16px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                background: "#fff",
                cursor: "pointer",
                fontSize: "0.875rem"
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "8px 16px",
                border: "none",
                borderRadius: "6px",
                background: "#002347",
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "0.875rem"
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditProductModal;
